/**
 * Procedures Management permissions.
 *
 * Module capability: Admin and ProceduresAdmin get everything. ProceduresEditor gets everything except
 * Settings.Manage (the seeded role rights in pg/identity/009). Any other user gets a capability only when
 * they hold the matching `Procedures.*` right (rights come from the identity service and can be granted
 * per user without a role).
 *
 * Note: the console API also requires one of the console roles (Admin, ProceduresAdmin, ProceduresEditor)
 * on top of the right, so a right on its own lets the page open but the API answers 403.
 *
 * The capability rules live in `computeProceduresPermissions` (pure, no hooks) so the route gates
 * (ProceduresGate via useProceduresPermissions) and the sidebar menu (proceduresMenu.tsx via
 * proceduresMenuVisibility) evaluate exactly the same thing. Which capability opens which page is
 * PROCEDURES_PAGE_ACCESS; both the routes and the menu read it, so they cannot drift apart.
 *
 * Per-procedure actions are narrowed further by `ProcedureDetailView.permissions` from the API
 * (an editor only sees procedures they collaborate on or whose owner department is theirs).
 */

import { useMemo } from 'react';
import { PROCEDURES_RIGHTS, PROCEDURES_ROLES } from '@actschurch/ccms-sdk';
import type { MenuItemVisibility } from '../../layouts/menu/types';
import { useEnrichedAuth } from '../useEnrichedAuth';

export { PROCEDURES_RIGHTS, PROCEDURES_ROLES };

export const PROCEDURES_ALL_ROLES = ['Admin', 'ProceduresAdmin', 'ProceduresEditor'];
export const PROCEDURES_FULL_ACCESS_ROLES = ['Admin', 'ProceduresAdmin'];

interface ProceduresAccessChecks {
  hasRole: (role: string) => boolean;
  hasPermission: (right: string) => boolean;
}

/** Every Procedures capability for a user, from plain role / right checks (no React). */
export function computeProceduresPermissions({ hasRole, hasPermission }: ProceduresAccessChecks) {
  const isFullAccess = hasRole('Admin') || hasRole('ProceduresAdmin');
  const isEditor = hasRole('ProceduresEditor');

  /** Capability = full access, or the editor role when it grants it by default, or the explicit right. */
  const can = (right: string, editorDefault: boolean) => isFullAccess || (isEditor && editorDefault) || hasPermission(right);

  const canRead = can(PROCEDURES_RIGHTS.Read, true);
  const canManage = can(PROCEDURES_RIGHTS.Manage, true);
  const canPublish = can(PROCEDURES_RIGHTS.Publish, true);
  const canImport = can(PROCEDURES_RIGHTS.Import, true);
  const canViewRuns = can(PROCEDURES_RIGHTS.RunsRead, true);
  const canManageSettings = can(PROCEDURES_RIGHTS.SettingsManage, false);

  return {
    canRead,
    canManage,
    canPublish,
    canImport,
    canViewRuns,
    canManageSettings,
    isFullAccess,
    isEditor,
    /** Holds a console role (the API needs one of these on every admin call). */
    hasConsoleRole: isFullAccess || isEditor,
  };
}

export type ProceduresCapabilities = ReturnType<typeof computeProceduresPermissions>;

export function useProceduresPermissions() {
  const { user, hasRole, hasPermission } = useEnrichedAuth();

  const perms = useMemo(() => computeProceduresPermissions({ hasRole, hasPermission }), [hasRole, hasPermission]);

  return {
    ...perms,
    userId: user?.id as string | undefined,
  };
}

export type ProceduresPermissions = ReturnType<typeof useProceduresPermissions>;

/** Who may open a Procedures page: one predicate per page, shared by the routes and the menu. */
export type ProceduresPageAccess = (p: ProceduresCapabilities) => boolean;

export const PROCEDURES_PAGE_ACCESS = {
  dashboard: (p) => p.canRead,
  list: (p) => p.canRead,
  editor: (p) => p.canRead,
  import: (p) => p.canImport,
  runs: (p) => p.canViewRuns,
  problems: (p) => p.canViewRuns,
  qrSheet: (p) => p.canRead,
  categories: (p) => p.canRead,
  settings: (p) => p.canManageSettings,
} satisfies Record<string, ProceduresPageAccess>;

/**
 * Menu visibility that matches the route gate: the menu context carries the same roles and rights
 * (user.roles / user.rights) that useEnrichedAuth checks, so a page shows in the menu exactly when
 * ProceduresGate would let the person in. Pass several pages for a section.
 */
export function proceduresMenuVisibility(...pages: ProceduresPageAccess[]): MenuItemVisibility {
  return (ctx) => {
    const roles = ctx.userRoles ?? [];
    const rights = ctx.userPermissions ?? [];
    const perms = computeProceduresPermissions({
      hasRole: (role) => roles.includes(role),
      hasPermission: (right) => rights.includes(right),
    });
    return pages.some((allow) => allow(perms));
  };
}
